/* eslint-disable import/no-named-as-default-member */
import { StateCreator } from 'zustand';
import chatApi from 'lib/api/chat';
import produce from 'immer';
import alertMessage from 'global/AlertMessage';
import { IChatStore, IChatAction, IRoomAction } from './types';
import { useGetChatUnreadCount } from './useGetChatUnreadCount';

const actions: StateCreator<
  IChatStore & IChatAction & IRoomAction,
  [],
  [],
  IChatAction
> = (set, get) => ({
  fetchMessage: async (params) =>
    new Promise((resolve) => {
      const { chatId, roomId, type, isFetchPrev, isFetchNext, ...restParams } =
        params;

      if (isFetchPrev) {
        set(
          produce((state: IChatStore) => {
            state.conversation.isLoadingPrevMessage = true;
          }),
        );
      } else if (isFetchNext) {
        set(
          produce((state: IChatStore) => {
            state.conversation.isLoadingNextMessage = true;
          }),
        );
      } else {
        set(
          produce((state: IChatStore) => {
            state.conversation.isLoading = true;
          }),
        );
      }

      let request = chatApi.getPersonalMessage;

      switch (type) {
        case 'group':
          request = chatApi.getGroupMessage;
          break;
        case 'broadcast':
          request = chatApi.getBroadcastMessage;
          break;
        default:
          request = chatApi.getPersonalMessage;
      }

      const id = type === 'personal' ? chatId : roomId;

      request(id, restParams)
        .then((res) => {
          const { data } = res.data;

          let messages = [];

          const conversation = get().conversation.messages[chatId];
          const prevCursorId = conversation ? conversation?.cursor?.prev : '';

          if (conversation && isFetchPrev) {
            messages = [...conversation.messages, ...data.messages];
          } else if (conversation && isFetchNext) {
            messages = [...data.messages, ...conversation.messages];
          } else {
            messages = data.messages;
          }

          const hasMore = data.is_more;

          set(
            produce((state: IChatStore) => {
              state.conversation.messages[chatId] = {
                ...data,
                cursor: {
                  ...data.cursor,
                  prev: isFetchNext ? prevCursorId : data?.cursor?.prev,
                },
                messages,
                is_more: hasMore,
              };
              state.conversation.isLoading = false;
              state.conversation.isLoadingPrevMessage = false;
              state.conversation.isLoadingNextMessage = false;
            }),
          );

          resolve(data);
        })
        .catch(() => {
          set(
            produce((state: IChatStore) => {
              state.conversation.isLoading = false;
              state.conversation.isLoadingPrevMessage = false;
              state.conversation.isLoadingNextMessage = false;
            }),
          );
        });
    }),
  pushMessage: async (params) => {
    const { message, chatId } = params;

    const {
      conversation: { localMessages, messages },
    } = get();

    const currentLocalMessage = localMessages[chatId];
    // remove message from local message
    if (currentLocalMessage) {
      const localMessageIndex = currentLocalMessage.findIndex(
        (msg) => msg.reference_id === params?.reference_id,
      );

      if (localMessageIndex > -1) {
        set(
          produce((state: IChatStore) => {
            state.conversation.localMessages[chatId].splice(
              localMessageIndex,
              1,
            );
          }),
        );
      }
    }

    const currentMessage = messages[chatId];
    // save message to store
    if (currentMessage) {
      const isOnConvo = currentMessage.messages.some(
        (item) => item.id === message.id,
      );
      if (!isOnConvo) {
        set(
          produce((state: IChatStore) => {
            state.conversation.messages[chatId].messages.unshift(message);
            if (params.room_status?.is_mentioned) {
              const hasMentioned = currentMessage.mentioned_message_ids;
              if (hasMentioned) {
                state.conversation.messages[chatId].mentioned_message_ids.push(
                  message?.id,
                );
              } else {
                state.conversation.messages[chatId].mentioned_message_ids = [
                  message?.id,
                ];
              }
            }
          }),
        );
      }
    } else {
      set(
        produce((state: IChatStore) => {
          state.conversation.messages[chatId] = {
            messages: [message],
            cursor: {
              prev: '',
              next: '',
            },
            is_more: false,
            mentioned_message_ids: [],
          };
        }),
      );
    }
  },
  selectMessage: (params) => {
    const { messageId, chatId, checked, align } = params;
    const { conversation } = get();

    if (conversation.selected.messageIds.length >= 10 && checked) {
      alertMessage({
        content: 'You can select up to 10 messages',
        messageType: 'error',
        alertType: 'snackbar',
        hasCloseIcon: true,
      });
      return;
    }

    const chatIndex = conversation.messages[chatId].messages.findIndex(
      (convo) => convo.id === messageId,
    );

    set(
      produce((state: IChatStore) => {
        const index = conversation.selected.messageIds.indexOf(messageId);
        if (checked && index < 0) {
          state.conversation.selected.messageIds.push(messageId);
          state.conversation.selected.messageAlign.push(align);
        } else if (index >= 0) {
          state.conversation.selected.messageIds.splice(index, 1);
          state.conversation.selected.messageAlign.splice(index, 1);
        }
        state.conversation.messages[chatId].messages[chatIndex].selected =
          checked;
      }),
    );
  },
  cancelSelect: (params) => {
    const { chatId } = params;

    set(
      produce((state: IChatStore) => {
        state.conversation.selected.messageIds = [];
        state.conversation.selected.messageAlign = [];
        state.conversation.messages[chatId].messages =
          state.conversation.messages[chatId].messages.map((message) => ({
            ...message,
            selected: false,
          }));
      }),
    );
  },
  doResetSelectedConversation: () => {
    set(
      produce((state: IChatStore) => {
        state.conversation.selected.messageIds = [];
        state.conversation.selected.messageAlign = [];
      }),
    );
  },
  deleteMessages: (params) =>
    new Promise((resolve, reject) => {
      const { chatId, messageIds } = params;
      chatApi
        .deleteBulkChatMessage(messageIds)
        .then((res) => {
          const { data, error } = res.data;

          if (res.status === 200) {
            const { messages } = get().conversation.messages[chatId];

            set(
              produce((state: IChatStore) => {
                // eslint-disable-next-line array-callback-return
                messageIds.map((messageId) => {
                  const deletedMessageIdx = messages.findIndex(
                    (message) => messageId === message.id,
                  );

                  if (deletedMessageIdx > -1) {
                    // set flag to deleted
                    state.conversation.messages[chatId].messages[
                      deletedMessageIdx
                    ].body.conversation.flag.is_deleted = true;

                    // change text message
                    state.conversation.messages[chatId].messages[
                      deletedMessageIdx
                    ].body.conversation.text = 'You deleted this message';
                  }
                });
              }),
            );
            resolve(data);
          } else {
            reject(error);
          }
        })
        .catch((error) => {
          reject(error);
        });
    }),
  clearMessages: (chatId) =>
    new Promise((resolve, reject) => {
      chatApi
        .clearChat(chatId)
        .then((res) => {
          const { data, error } = res.data;

          if (data) {
            set(
              produce((state: IChatStore) => {
                state.conversation.messages[chatId] = {
                  cursor: {
                    prev: '',
                    next: '',
                  },
                  messages: [],
                  is_more: false,
                  mentioned_message_ids: [],
                };
              }),
            );
            resolve(data);
          } else {
            reject(error);
          }
        })
        .catch(() => {});
    }),
  cancelReply: () => {
    set({
      reply: {
        to_id: 0,
        visible: false,
      },
    });
  },
  replyMessage: (messageId) => {
    set({
      reply: {
        to_id: messageId,
        visible: true,
      },
    });
  },
  sendMessage: (params, chatId) =>
    new Promise((resolve) => {
      chatApi
        .sendChatV2(params)
        .then((res) => {
          const { data, error } = res.data;
          if (data) {
            const { message } = data;
            get()
              .findAndClearLocalMessages(chatId, params?.reference_id)
              .then(() => {
                get().pushMessage({
                  message,
                  chatId,
                  reference_id: params?.reference_id,
                });
              });
            resolve(data);
          } else {
            resolve(error);
          }
        })
        .catch((err) => {
          get().changeLocalMessageStatus(
            chatId,
            params.reference_id,
            'MESSAGE_STATUS_ERROR',
          );
          resolve(err);
        });
    }),
  pushLocalMessage: (params) => {
    if (get().conversation.localMessages[params.data.chatId]) {
      set(
        produce((state: IChatStore) => {
          state.conversation.localMessages[params.data.chatId].push(params);
        }),
      );
    } else {
      set(
        produce((state: IChatStore) => {
          state.conversation.localMessages[params.data.chatId] = [params];
        }),
      );
    }
  },
  changeLocalMessageStatus: (chatId, referenceId, status) => {
    const localMessageIndex = get().conversation.localMessages[
      chatId
    ].findIndex((msg) => msg.reference_id === referenceId);

    if (localMessageIndex > -1) {
      set(
        produce((state: IChatStore) => {
          state.conversation.localMessages[chatId][
            localMessageIndex
          ].data.status = status;
        }),
      );
    }
  },
  findAndClearLocalMessages: (chatId, reference_id) => {
    const {
      conversation: { localMessages },
    } = get();

    return new Promise((res, rej) => {
      const currentLocalMessage = localMessages[chatId];
      // remove message from local message
      if (currentLocalMessage) {
        const localMessageIndex = currentLocalMessage.findIndex(
          (msg) => msg.reference_id === reference_id,
        );

        if (localMessageIndex > -1) {
          set(
            produce((state: IChatStore) => {
              state.conversation.localMessages[chatId].splice(
                localMessageIndex,
                1,
              );
            }),
          );
          res();
        } else {
          rej();
        }
      }
    });
  },
  clearLocalMessages: (chatId) => {
    set(
      produce((state: IChatStore) => {
        state.conversation.localMessages[chatId] = null;
      }),
    );
  },
  readMessage: (chatId, roomType) =>
    new Promise((resolve) => {
      chatApi
        .readChat(chatId)
        .then((res) => {
          const { data } = res.data;
          const { fetchUnreadCount } = useGetChatUnreadCount.getState();

          // update the chat notification counter
          fetchUnreadCount();

          set(
            produce((state: IChatStore) => {
              const roomIdx = get().rooms[roomType].list.findIndex(
                // eslint-disable-next-line eqeqeq
                (room) => `${room.id}` === `${chatId}`,
              );
              if (roomIdx > -1) {
                state.rooms[roomType].list[roomIdx].unread_count = 0;
                state.rooms[roomType].list[roomIdx].status.is_mentioned = false;
              }
            }),
          );

          resolve(data);
        })
        .catch(() => {});
    }),
  readMentionedMessage: (chatId, messageId) => {
    set(
      produce((state: IChatStore) => {
        const messageIndex = state.conversation.messages[
          chatId
        ].mentioned_message_ids.findIndex((item) => item === messageId);
        if (messageIndex > -1) {
          state.conversation.messages[chatId].mentioned_message_ids.splice(
            messageIndex,
            1,
          );
        }
      }),
    );
  },
  forwardMessage: (params) =>
    new Promise((resolve, reject) => {
      const { chatId, ...bodyParams } = params;
      chatApi
        .forwardChat(bodyParams)
        .then(async (res) => {
          const { data } = res.data;
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    }),
  updateMessageFromWS: (params) => {
    const { chatId, messageId, message } = params;

    set(
      produce((state: IChatStore) => {
        if (state.conversation.messages[chatId]) {
          const messageIdx = state.conversation.messages[
            chatId
          ].messages.findIndex((msg) => msg.id === messageId);

          if (messageIdx > -1) {
            state.conversation.messages[chatId].messages[messageIdx] = message;
          }
        }
      }),
    );
  },
});

export default actions;
